// TarjetasRadio — el ÚNICO control de «elegir un valor» de toda la app
// (plan_migracion_reui.md §1.8, decisión 2; interfaz cerrada en §2.h.1).
// Composición copiada TAL CUAL de c-radio-group-7
// (https://reui.io/preview/base/components/c-radio-group-7?ref=mcp) para el
// caso de 1 columna y de c-radio-group-11
// (https://reui.io/preview/base/components/c-radio-group-11?ref=mcp) para el
// grid de N columnas — sin estilo propio, solo mapeo de datos:
// RadioGroup > FieldLabel htmlFor > Field orientation="horizontal" >
// FieldContent (FieldTitle + adorno, FieldDescription) + RadioGroupItem.
//
// Usos previstos (R1/R2/R3, sin cambiar esta interfaz): ¿Salta?, Entregada
// a, Requiere póliza EC, los 5 estados de Estado de redes (con `adorno` =
// Badge «actual»), Posponer, «Mostrar» y «Orden» del popover del Avivador.
//
// `role=radio` y `aria-checked` los pone Base UI Radio.Root (RadioGroupItem,
// ui/radio-group.tsx) — de ahí dependen `primerCampo`
// (panel-avivador.tsx:82-93) y `cambiaCampo` (panel-avivador-accion.tsx:
// 110-119); esta capa no los toca.
import { useId, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Field, FieldContent, FieldDescription, FieldLabel, FieldTitle } from '@/components/ui/field';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

export interface OpcionTarjeta<V extends string> {
  valor: V;
  titulo: ReactNode; // FieldTitle
  descripcion?: ReactNode; // FieldDescription (la fecha de Posponer, el hint de R4, el conteo de «Mostrar»)
  adorno?: ReactNode; // al lado del título (Badge «actual» de R4)
  deshabilitada?: boolean;
}

export interface TarjetasRadioProps<V extends string> {
  valor: V | null;
  onValorChange: (v: V) => void;
  opciones: readonly OpcionTarjeta<V>[];
  columnas?: 1 | 2 | 3; // grid-cols-N (el className del RadioGroup; c-radio-group-11)
  disabled?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  /** prefijo de los id (FieldLabel htmlFor <-> RadioGroupItem id), de useId() si falta. */
  idBase?: string;
  // Única excepción a la interfaz cerrada (O6 de la QA del 28/9, Juan): el
  // diálogo Estado de redes medía 101,5 px más que la rc.1 solo por el aire
  // de este control (gap-3 del RadioGroup + *:p-3 de FieldLabel + text-sm en
  // título/descripción). `compacto` reproduce las medidas de la rc.1 —
  // separación 6 px, relleno 10×12 px, título 13 px, descripción 12 px, 2 px
  // entre ambos — SOLO en el caller que lo pide. Default false/undefined: no
  // cambia un píxel del render de los demás usos (¿Salta?, Entregada a,
  // Requiere póliza EC, Posponer, Mostrar/Orden del Avivador, Corregir
  // hitos).
  compacto?: boolean;
}

// Literales (no template strings) para que Tailwind los detecte en el build.
const CLASE_COLUMNAS: Record<1 | 2 | 3, string> = {
  1: '',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
};

export function TarjetasRadio<V extends string>({
  valor,
  onValorChange,
  opciones,
  columnas = 1,
  disabled,
  compacto = false,
  idBase,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}: TarjetasRadioProps<V>) {
  const generatedId = useId();
  const prefix = idBase ?? generatedId;

  return (
    <RadioGroup
      value={valor ?? undefined}
      onValueChange={v => onValorChange(v as V)}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      className={cn(CLASE_COLUMNAS[columnas], compacto && 'gap-1.5')}
    >
      {opciones.map(opcion => {
        const id = `${prefix}-${opcion.valor}`;
        // Ajuste 8 (R-5/C-20): data-disabled en el Field cuando la opción o
        // el grupo están deshabilitados — así la tarjeta se ve apagada
        // (FieldTitle) y no reacciona al hover (FieldLabel), aunque el
        // usuario no la haya tocado a mano.
        const opcionDeshabilitada = disabled || opcion.deshabilitada;
        return (
          // El relleno de la tarjeta sale de *:data-[slot=field]:p-3 en
          // FieldLabel (ui/field.tsx): compacto agrega py-2.5/px-3 acá
          // mismo, con el MISMO selector (*:data-[slot=field]:), así el
          // orden canónico de Tailwind (p antes que px/py dentro de
          // @layer utilities) decide el override sin depender del orden en
          // el HTML ni de que tailwind-merge entienda el selector — se
          // verificó compilando este bloque con `compile()` de tailwindcss
          // (mismo p-3 seguido de px-3/py-2.5 en el CSS generado).
          <FieldLabel
            key={opcion.valor}
            htmlFor={id}
            className={cn(compacto && '*:data-[slot=field]:py-2.5 *:data-[slot=field]:px-3')}
          >
            <Field orientation="horizontal" data-disabled={opcionDeshabilitada || undefined}>
              <FieldContent className={cn(compacto && 'gap-0.5')}>
                {/* leading explícito: text-[Npx] no trae line-height y
                    tailwind-merge descarta el leading-normal de la base. */}
                <FieldTitle className={cn(compacto && 'text-[13px] leading-snug')}>
                  {opcion.titulo}
                  {opcion.adorno}
                </FieldTitle>
                {opcion.descripcion && (
                  <FieldDescription className={cn(compacto && 'text-[12px] leading-snug')}>{opcion.descripcion}</FieldDescription>
                )}
              </FieldContent>
              <RadioGroupItem value={opcion.valor} id={id} disabled={opcionDeshabilitada} />
            </Field>
          </FieldLabel>
        );
      })}
    </RadioGroup>
  );
}
